import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { nonNegativeParam, withDayTimeZone } from '@/lib/utils'
import { dealKeys } from '@/hooks/api/deal-keys'
import { invalidateContactViews } from '@/hooks/api/contacts'
import type {
  PropertyListItem,
  PropertyDetailPayload,
  PropertyRevisionListPayload,
  PaginatedResponse,
  PhotoProcessingStatus,
} from '@repo/types'
import type { EntityNote } from './notes'

/**
 * Typed data-access layer for the admin `/properties` endpoints.
 *
 * Centralises query keys, URL building, the response envelope and cache
 * invalidation. Mutations own the URL + envelope + invalidation (the drift-prone
 * part); page-specific reactions (toast copy, selection resets, redirects) stay
 * at the call site via `mutate(vars, { onSuccess })`. Cache contract:
 *   - lists  → ['properties', …]
 *   - detail → ['property', id]
 *   - notes  → ['property-notes', id]
 *   - cities → ['property-cities']
 *
 * The property form's create/update save mutation lives in its own hook
 * (`pages/properties/property-form/usePropertySave.tsx`): it orchestrates
 * staged-photo upload + create-then-redirect that is irreducibly page-specific,
 * and invalidates through `invalidatePropertyViews` like `useUpdateProperty`.
 */

export interface PropertyListFilters {
  page: number
  /** Archive tab — sends `archived=true` and suppresses the status filter. */
  archived: boolean
  status?: string
  propertyType?: string
  transactionType?: string
  /** Typ ponuky (EXCLUSIVE | NON_EXCLUSIVE | OFF_MARKET). */
  dealType?: string
  city?: string
  priceMin?: string
  priceMax?: string
  createdFrom?: string
  createdTo?: string
  /** When true, scopes to the current user and overrides `agentId`. */
  onlyMine?: boolean
  agentId?: string
  /** Filter podľa pobočky (OWNER+; API ho aplikuje v rámci viditeľnostného scope). */
  branchId?: string
  search?: string
  sortBy: string
  sortOrder: 'asc' | 'desc'
}

export const propertyKeys = {
  /** Prefix matching every paginated property list — use for broad invalidation. */
  lists: ['properties'] as const,
  list: (filters: PropertyListFilters) => ['properties', filters] as const,
  detail: (id: number | undefined) => ['property', id] as const,
  notes: (id: number | undefined) => ['property-notes', id] as const,
  /** Fotogaléria inzerátu. Vlastní ju `PropertyMediaGallery`, ale `PortalPublicationCard`
   *  sa na ten istý kľúč prihlasuje (zmena fotiek mení výsledok publish validácie),
   *  takže kľúč musí mať jediné miesto definície. */
  media: (id: number | undefined) => ['property-media', id] as const,
  history: (id: number | undefined) => ['property-history', id] as const,
  cities: ['property-cities'] as const,
  options: ['properties-select'] as const,
}

/**
 * Nehnuteľnosť sa zobrazuje v detaile a histórii, v zoznamoch, filtri miest a výberoch,
 * v obchodoch (detail, zoznam, pipeline — názov, provízia, predávajúci = vlastníci, provízia
 * prepočíta aj rozdelenie provízie) a pri osobe-vlastníkovi — po zmene obnoviť všetky. Nový vlastník
 * môže byť kontakt, ktorého trigger povýši na zákazníka (zoznam, Reporty, dashboard).
 */
export function invalidatePropertyViews(qc: QueryClient, id: number | undefined) {
  qc.invalidateQueries({ queryKey: propertyKeys.detail(id) })
  qc.invalidateQueries({ queryKey: propertyKeys.history(id) })
  qc.invalidateQueries({ queryKey: propertyKeys.lists })
  qc.invalidateQueries({ queryKey: propertyKeys.cities })
  qc.invalidateQueries({ queryKey: propertyKeys.options })
  qc.invalidateQueries({ queryKey: dealKeys.details })
  qc.invalidateQueries({ queryKey: dealKeys.lists })
  qc.invalidateQueries({ queryKey: dealKeys.pipeline })
  qc.invalidateQueries({ queryKey: ['commission-splits'] })
  invalidateContactViews(qc)
}

/** Spoločný `mutationKey` optimistických zápisov do galérie (mazanie, titulná, poradie) —
 *  kým niektorá beží, `usePropertyPhotos` sa neobnovuje (obnova by zápis prepísala). */
export const propertyPhotosMutationKey = (id: number | undefined) => ['property-photos-mutation', id] as const

export function useProperties(filters: PropertyListFilters) {
  return useQuery({
    queryKey: propertyKeys.list(filters),
    queryFn: async () => {
      const params = new URLSearchParams()
      params.set('page', String(filters.page))
      params.set('limit', '20')
      params.set('sortBy', filters.sortBy)
      params.set('sortOrder', filters.sortOrder)
      if (filters.archived) params.set('archived', 'true')
      if (filters.status && !filters.archived) params.set('status', filters.status)
      if (filters.propertyType) params.set('propertyType', filters.propertyType)
      if (filters.transactionType) params.set('transactionType', filters.transactionType)
      if (filters.dealType) params.set('dealType', filters.dealType)
      if (filters.city) params.set('city', filters.city)
      const priceMin = nonNegativeParam(filters.priceMin)
      if (priceMin) params.set('priceMin', priceMin)
      const priceMax = nonNegativeParam(filters.priceMax)
      if (priceMax) params.set('priceMax', priceMax)
      if (filters.createdFrom) params.set('createdFrom', filters.createdFrom)
      if (filters.createdTo) params.set('createdTo', filters.createdTo)
      if (filters.onlyMine) params.set('onlyMine', 'true')
      else if (filters.agentId) params.set('agentId', filters.agentId)
      if (filters.branchId) params.set('branchId', filters.branchId)
      if (filters.search) params.set('search', filters.search)
      const { data } = await api.get<PaginatedResponse<PropertyListItem>>(`/properties?${withDayTimeZone(params)}`)
      return data
    },
  })
}

/** Fotka inzerátu tak, ako ju vracia `GET /media?propertyId=…&type=PHOTO`. */
export interface PropertyPhoto {
  id: string
  url: string
  title: string | null
  altText: string | null
  isCover: boolean
  sortOrder: number
  type: string
  /** PROCESSING = dokončuje ju media worker (sivá dlaždica), FAILED = nepodarilo sa ju spracovať. */
  processingStatus: PhotoProcessingStatus
  /** Čistý originál bez vodoznaku (editor fotiek); legacy fotky ho nemajú. */
  originalUrl: string | null
  originalThumbnailUrl: string | null
  legacyBaked: boolean
  width: number | null
  height: number | null
  /** Editor fotiek: obsah upravený cez AI (odznak „AI"), želanie nápisu, pôvodná verzia pred úpravou. */
  aiEdited: boolean
  visualizationLabel: boolean
  preEditOriginalUrl: string | null
}

/**
 * Fotogaléria inzerátu. Kľúč AJ dotaz musia mať jediné miesto definície: na tú istú
 * cache entry sa prihlasuje `PropertyMediaGallery` (vlastník — mutácie, optimistické
 * zápisy) aj `PortalPublicationCard` (zmena fotiek mení výsledok publish validácie).
 * Dva `useQuery` s rovnakým kľúčom a rôznym `queryFn` sa v React Query bijú —
 * posledné zaregistrované options vyhrajú, takže vlastníkov refetch by spadol na
 * cudzí (alebo `skipToken`) fetcher a galéria by prestala obnovovať dáta.
 */
export function usePropertyPhotos(propertyId: number | undefined) {
  const qc = useQueryClient()
  return useQuery({
    queryKey: propertyKeys.media(propertyId),
    queryFn: async () => {
      // limit = maximum MediaFiltersDto; default 50 by pri väčšej galérii odrezal rozpracované
      // fotky (pribúdajú na koniec poradia) — nevidel by ich zámok zverejnenia ani obnova.
      const { data } = await api.get(`/media?propertyId=${propertyId}&type=PHOTO&includeProcessing=1&limit=100`)
      return ((data.data ?? data) as PropertyPhoto[]).sort((a, b) => a.sortOrder - b.sortOrder)
    },
    enabled: !!propertyId,
    // Kým media worker niektorú fotku dokončuje, galéria sa obnovuje (dlaždica → fotka).
    refetchInterval: (query) => {
      if (!query.state.data?.some((p) => p.processingStatus === 'PROCESSING')) return false
      // Počas optimistického zápisu by obnova vrátila staré dáta zo servera. Mutácie v `onSettled`
      // invalidujú kľúč a refetch po ich dobehnutí interval prepočíta — obnova sa rozbehne sama.
      if (qc.isMutating({ mutationKey: propertyPhotosMutationKey(propertyId) }) > 0) return false
      // Výpadok API: globálny interceptor toastuje každú chybu — neopakuj ju každé 3 s.
      return query.state.status === 'error' ? 30_000 : 3000
    },
  })
}

export function useProperty(
  id: number | undefined,
  options?: { enabled?: boolean; refetchOnWindowFocus?: boolean; refetchOnReconnect?: boolean },
) {
  return useQuery({
    queryKey: propertyKeys.detail(id),
    queryFn: async () => {
      const { data } = await api.get(`/properties/${id}`)
      return (data.data ?? data) as PropertyDetailPayload
    },
    enabled: options?.enabled ?? !!id,
    refetchOnWindowFocus: options?.refetchOnWindowFocus,
    refetchOnReconnect: options?.refetchOnReconnect,
    retry: (failureCount, err: unknown) => {
      // Do not retry on 404 — property was deleted or caller has no access.
      if ((err as { response?: { status?: number } })?.response?.status === 404) return false
      return failureCount < 2
    },
  })
}

/** Active-property options for selector dropdowns (e.g. the calendar event form).
 * Returns the raw response (callers handle `?.data ?? []`). Key kept verbatim. */
export function usePropertyOptions(enabled = true) {
  return useQuery({
    queryKey: propertyKeys.options,
    queryFn: async () => {
      const { data } = await api.get('/properties?limit=100&status=ACTIVE')
      return data
    },
    enabled,
  })
}

/** Distinct cities across the tenant's properties — list-page filter dropdown. */
export function usePropertyCities() {
  return useQuery<string[]>({
    queryKey: propertyKeys.cities,
    queryFn: async () => {
      const { data } = await api.get('/properties/cities')
      return Array.isArray(data) ? data : data?.data ?? []
    },
  })
}

/** Patch one property (owners, commission, …). `skipErrorToast` = volajúci ukáže chybu sám. */
export function useUpdateProperty({ skipErrorToast = false } = {}) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: Record<string, unknown> }) =>
      api.patch(`/properties/${id}`, payload, { skipErrorToast }),
    onSuccess: (_res, { id }) => invalidatePropertyViews(qc, id),
  })
}

/* ------------------------------------------------------------------ *
 * List-page mutations (single + bulk). `id`/`ids` are the mutate variable;
 * each invalidates the list. Page owns toast copy + selection reset.
 * ------------------------------------------------------------------ */

/** Archive (soft-delete) a single property. */
export function useArchiveProperty() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete(`/properties/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: propertyKeys.lists }),
  })
}

/** Restore a property from the archive. Invalidates lists (+ detail). */
export function useRestoreProperty() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.post(`/properties/${id}/restore`),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: propertyKeys.lists })
      qc.invalidateQueries({ queryKey: propertyKeys.detail(id) })
    },
  })
}

/** Permanently delete an archived property. */
export function usePermanentDeleteProperty() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete(`/properties/${id}/permanent`),
    onSuccess: () => qc.invalidateQueries({ queryKey: propertyKeys.lists }),
  })
}

/** Bulk status change (Promise.all of PATCH). */
export function useBulkSetPropertyStatus() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ ids, status }: { ids: number[]; status: string }) =>
      Promise.all(ids.map((id) => api.patch(`/properties/${id}`, { status }))),
    onSuccess: () => qc.invalidateQueries({ queryKey: propertyKeys.lists }),
  })
}

/**
 * Bulk publish (Promise.allSettled of POST publish; the caller toasts a summary). Same endpoint as the
 * mobile "Publikovať": the API rejects off-market listings, which a status PATCH would publish.
 */
export function useBulkPublishProperties() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (ids: number[]) =>
      Promise.allSettled(ids.map((id) => api.post(`/properties/${id}/publish`, undefined, { skipErrorToast: true }))),
    onSuccess: () => qc.invalidateQueries({ queryKey: propertyKeys.lists }),
  })
}

/** Bulk restore (Promise.all of POST restore). */
export function useBulkRestoreProperties() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (ids: number[]) =>
      Promise.all(ids.map((id) => api.post(`/properties/${id}/restore`))),
    onSuccess: () => qc.invalidateQueries({ queryKey: propertyKeys.lists }),
  })
}

/** Bulk permanent delete (Promise.allSettled — some may fail on FK links; the caller toasts a summary). */
export function useBulkPermanentDeleteProperties() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (ids: number[]) =>
      Promise.allSettled(ids.map((id) => api.delete(`/properties/${id}/permanent`, { skipErrorToast: true }))),
    onSuccess: () => qc.invalidateQueries({ queryKey: propertyKeys.lists }),
  })
}

/** Bulk reassign agent (Promise.allSettled of PATCH; the caller toasts a summary). */
export function useBulkReassignProperties() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ ids, agentId }: { ids: number[]; agentId: string }) =>
      Promise.allSettled(ids.map((id) => api.patch(`/properties/${id}`, { agentId }, { skipErrorToast: true }))),
    onSuccess: () => qc.invalidateQueries({ queryKey: propertyKeys.lists }),
  })
}

/* ------------------------------------------------------------------ *
 * Form-page sub-hooks: notes (mutácie v `hooks/api/notes.ts`) + portal publication.
 * ------------------------------------------------------------------ */

/** Internal notes attached to a property. */
export function usePropertyNotes(id: number | undefined) {
  return useQuery<EntityNote[]>({
    queryKey: propertyKeys.notes(id),
    queryFn: async () => {
      const { data } = await api.get(`/properties/${id}/notes`)
      return Array.isArray(data) ? data : data?.data ?? []
    },
    enabled: !!id,
  })
}

/** História zmien nehnuteľnosti (revízie s diffom + refs pre UUID hodnoty). */
export function usePropertyRevisions(id: number | undefined) {
  return useQuery<PropertyRevisionListPayload>({
    queryKey: propertyKeys.history(id),
    queryFn: async () => {
      const { data } = await api.get(`/properties/${id}/history`)
      return data as PropertyRevisionListPayload
    },
    enabled: !!id,
  })
}

/** Obnovenie staršej verzie z histórie zmien — invaliduje detail aj históriu. */
export function useRestorePropertyRevision(id: number | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (revisionId: string) =>
      api.post(`/properties/${id}/history/${revisionId}/restore`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: propertyKeys.detail(id) })
      qc.invalidateQueries({ queryKey: propertyKeys.history(id) })
      qc.invalidateQueries({ queryKey: propertyKeys.lists })
    },
  })
}

/** Publish a property to an external portal. Page supplies `onChanged`. */
export function usePublishPropertyPortal(id: number | undefined) {
  return useMutation({
    mutationFn: (portal: string) => api.post(`/properties/${id}/portals/${portal}/publish`),
  })
}

/** Withdraw a property from an external portal. */
export function useWithdrawPropertyPortal(id: number | undefined) {
  return useMutation({
    mutationFn: (portal: string) => api.post(`/properties/${id}/portals/${portal}/withdraw`),
  })
}
